import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { AccountService, ConfigService } from '@core/services';
import { HelpersBffService } from '@core/services/bff/helpers.service';
import { BillingV2Service } from '@core/services/lingo2-account/billing-v2.service';
import {
  BillingApiReportCombinedProductResponseDto,
  BillingApiReportCombinedResponseDto,
} from '@lingo2-billing-sdk/models';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { loadMe } from '@store/actions/profile.actions';
import { getMe } from '@store/reducers/profile.reducer';
import { Collection, CurrencyEnum, UserService, UserStatusEnum } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, distinctUntilChanged, map, tap } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export interface PaymentHistoryData {
  id: string;
  date: Date | number;
  operationTitle: string;
  operationDesc: string;
  amount: number;
  currency: CurrencyEnum;
  operationType: 'debit' | 'credit';
  status: string;
}

@Component({
  selector: 'app-payment-history',
  templateUrl: './payment-history.component.html',
  styleUrls: ['./payment-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentHistoryComponent extends ChangableComponent implements OnInit {
  protected readonly billingV2Service = inject(BillingV2Service);
  protected readonly deviceService = inject(DeviceDetectorService);
  private helpers = inject(HelpersBffService);
  private store = inject(Store);
  private configService = inject(ConfigService);

  @Input() history: BillingApiReportCombinedResponseDto[];

  @Input() type: 'modal' | 'page' = 'page';

  public offset: number;

  ngOnInit(): void {
    combineLatest([this.watchMyTimezone$, this.watchTimezones$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([timezone, timezones]) => {
          this.offset = timezones.find((el) => el.name == timezone)?.offset;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected get watchMyTimezone$() {
    return this.store.select(getMe).pipe(
      map((me) => me?.timezone),
      distinctUntilChanged(),
    );
  }

  protected get watchTimezones$() {
    return this.configService.timezones$.pipe(filter((x) => !!x));
  }

  public getDateFromTransaction(date: Date): Date {
    const utcDate = new Date(date);

    // Apply offset in milliseconds
    const offsetMillis = this.offset * 60 * 1000;
    const adjustedDate = new Date(utcDate.getTime() + offsetMillis);

    // Return adjusted date with no timezone info
    const isoWithoutZ = adjustedDate.toISOString().replace(/Z$/, '');
    return new Date(isoWithoutZ);
  }

  public formatAmount(operation: BillingApiReportCombinedResponseDto) {
    return this.billingV2Service.formatAmount(operation.amount, operation.currencyCode);
  }

  public getProductTypeTranslate(operation: BillingApiReportCombinedResponseDto) {
    if (operation.product && operation.product.type) {
      return operation.product.type + '.' + operation.type;
    }
    return operation.type;
  }

  public person(operation: BillingApiReportCombinedResponseDto) {
    return `<a class='account-name' target='_blank' href='${AccountService.accountRoute(operation.person).join('/')}'>${
      operation.person?.firstName + ' ' + operation.person?.lastName
    }</a>`;
  }

  public productLink(product: BillingApiReportCombinedProductResponseDto) {
    switch (product.type) {
      case 'user_service':
        return this.helpers.userServiceRouteUniversal(product as UserService);
      case 'collection':
        return LibraryRouter.collectionRouteUniversal(product as Collection);
      case 'meeting':
        return '/classes/join/' + product.slug;
    }
    return '';
  }

  public trackByFn(index: BillingApiReportCombinedResponseDto) {
    return index;
  }
}
